import { AttachmentFileType } from 'owa-attachment-file-types';
import type {
    AttachmentFileAttributes,
    Base64InlineImageFile,
    LocalComputerFile,
} from 'owa-attachment-file-types';
import { fetch } from 'owa-fetch';
import type AttachmentType from 'owa-service/lib/contract/AttachmentType';
import type FileAttachment from 'owa-service/lib/contract/FileAttachment';
import { blobToBase64 } from './attachmentUtils';
import { isSvgMimeType } from './sanitizeEmlHtml';

function isFileAttachment(attachment: AttachmentType): attachment is FileAttachment {
    const owsTypeName = (attachment as { OwsTypeName?: string; __type?: string }).OwsTypeName;
    const typeName = owsTypeName || (attachment as { __type?: string }).__type;

    return typeName === 'FileAttachment:#Exchange';
}

function normalizeContentId(contentId?: string): string | undefined {
    return contentId?.replace(/^<|>$/g, '');
}

function base64ToBlob(base64Content: string, contentType: string): Blob {
    const binaryString = atob(base64Content);
    const bytes = Uint8Array.from(binaryString, char => char.charCodeAt(0));

    return new Blob([bytes], { type: contentType });
}

function isTrustedAttachmentOriginalUrl(url: string): boolean {
    try {
        const parsedUrl = new URL(url, window.location.href);

        return (
            parsedUrl.protocol === 'blob:' ||
            parsedUrl.protocol === 'data:' ||
            parsedUrl.origin === window.location.origin
        );
    } catch {
        return false;
    }
}

async function getAttachmentBlob(fileAttachment: FileAttachment): Promise<Blob | undefined> {
    if (fileAttachment.Content) {
        return base64ToBlob(fileAttachment.Content, fileAttachment.ContentType ?? '');
    }

    if (
        fileAttachment.AttachmentOriginalUrl &&
        isTrustedAttachmentOriginalUrl(fileAttachment.AttachmentOriginalUrl)
    ) {
        try {
            const response = await fetch(fileAttachment.AttachmentOriginalUrl, {
                credentials: 'same-origin',
            });

            if (!response.ok) {
                return undefined;
            }

            return response.blob();
        } catch {
            return undefined;
        }
    }

    return undefined;
}

/**
 * Creates an inline compose image from Content-backed EML or URL-backed MSG attachment bytes,
 * keeping embedded images out of the ordinary attachment list when forwarding local messages.
 */
async function createInlineImageFile(
    fileAttachment: FileAttachment,
    contentType: string
): Promise<Base64InlineImageFile | undefined> {
    let content = fileAttachment.Content;
    let size = fileAttachment.Size ?? 0;

    if (!content) {
        const blob = await getAttachmentBlob(fileAttachment);
        if (!blob) {
            return undefined;
        }

        content = await blobToBase64(blob);
        size = fileAttachment.Size ?? blob.size;
    }

    return {
        fileType: AttachmentFileType.Base64InlineImage,
        name: fileAttachment.Name ?? 'inline-image',
        size,
        type: contentType,
        dataUri: `data:${contentType};base64,${content}`,
        tempId: normalizeContentId(fileAttachment.ContentId) ?? crypto.randomUUID(),
    };
}

async function createLocalComputerFile(
    fileAttachment: FileAttachment,
    contentType: string
): Promise<LocalComputerFile | undefined> {
    const blob = await getAttachmentBlob(fileAttachment);
    if (!blob) {
        return undefined;
    }

    return {
        fileType: AttachmentFileType.Local,
        name: fileAttachment.Name ?? 'attachment',
        size: fileAttachment.Size ?? blob.size,
        type: contentType || blob.type,
        fileObject: new File([blob], fileAttachment.Name ?? 'attachment', {
            type: contentType || blob.type,
        }),
    };
}

export default async function convertLocalItemAttachmentsToFileAttributes(
    attachments: AttachmentType[]
): Promise<AttachmentFileAttributes[]> {
    const fileAttributes: AttachmentFileAttributes[] = [];

    for (const attachment of attachments) {
        if (!isFileAttachment(attachment)) {
            continue;
        }

        const contentType = attachment.ContentType ?? '';
        const isInlineImage =
            !!attachment.IsInline &&
            contentType.startsWith('image/') &&
            !isSvgMimeType(contentType);

        if (isInlineImage) {
            const inlineImageFile = await createInlineImageFile(attachment, contentType);
            if (inlineImageFile) {
                fileAttributes.push({
                    file: inlineImageFile,
                    isInline: true,
                });
                continue;
            }
        }

        const localFile = await createLocalComputerFile(attachment, contentType);
        if (localFile) {
            fileAttributes.push({
                file: localFile,
                isInline: false,
            });
        }
    }

    return fileAttributes;
}
